import {useEffect, useState, type Dispatch, type SetStateAction} from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  Checkbox,
  FormControlLabel,
  Divider,
  Stack,
  styled,
  alpha,
} from '@mui/material';

import backendCommunicator from 'services/backendCommunicator';
import {type FishTypeResponse, type FishTypePost} from 'client';
import Alert from 'helpers/Alert';
import {type AxiosError} from 'axios';
import {SWRKeyEnum} from 'interfaces';

import {selectionHandler} from './AssignTree';

import useParseURL from 'hooks/useParseURL';

import {useGetPlanFishTypes} from 'hooks/swr';
import useIsMounted from 'hooks/useIsMounted';
import {mutate} from 'swr';
import {useTranslation} from 'react-i18next';

import ListSubheader from '@mui/material/ListSubheader';
import CircularCheckBox from 'components/InputMenu/Concession/CircularCheckbox';
import {type UpdateConcessionForm} from 'hooks/addEdit/useAddEditConcession';

export function FishTypeTable({
  concession,
  setConcession,
}: {
  concession: UpdateConcessionForm;
  setConcession: Dispatch<SetStateAction<UpdateConcessionForm>>;
}) {
  const {planId} = useParseURL();
  const isMounted = useIsMounted();
  const {data: fishTypes} = useGetPlanFishTypes({resourceId: planId});
  const {t} = useTranslation('common');

  const [fishType, setFishType] = useState<FishTypePost>({
    planId,
    name: '',
    growthModelId: '',
    feedConversionRatioModelId: '',
  });
  const [addFishType, setAddFishType] = useState<boolean>(false);
  const [deleteFishTypeId, setDeleteFishTypeId] = useState<string>();

  useEffect(() => {
    addFishType &&
      fishType &&
      backendCommunicator
        .addFishType(fishType)
        .then(() => {
          if (isMounted.current) {
            setAddFishType(false);
            setFishType({planId, name: '', growthModelId: '', feedConversionRatioModelId: ''});
            mutate([planId, SWRKeyEnum.GET_PLAN_FISH_TYPES]);
          }
        })
        .catch((error: AxiosError) => {
          Alert.error(error);
        });
  }, [addFishType, deleteFishTypeId]);

  useEffect(() => {
    deleteFishTypeId &&
      planId &&
      backendCommunicator
        .deleteFishType(planId, deleteFishTypeId)
        .then(() => {
          if (isMounted.current) {
            setDeleteFishTypeId(undefined);
            mutate([planId, SWRKeyEnum.GET_PLAN_FISH_TYPES]);
          }
        })
        .catch((error: AxiosError) => {
          Alert.error(error);
        });
  }, [deleteFishTypeId]);

  const TableLists = ({fishTypes}: {fishTypes: FishTypeResponse[]}) => {
    return (
      <>
        {fishTypes.map((fishType, index) => {
          const labelId = `enhanced-table-row-${index}`;
          const checked = concession.fishTypeIds.includes(fishType.id);
          return (
            <TableRow key={index}>
              <StyledTableCell>
                <StyledCheckBox
                  checked={checked}
                  inputProps={{'aria-labelledby': labelId}}
                  onChange={() => {
                    const fishTypeIds = selectionHandler(concession.fishTypeIds, fishType.id);
                    setConcession({
                      ...concession,
                      fishTypeIds,
                      assignToAllFishTypes: checked ? false : concession.assignToAllFishTypes,
                    });
                  }}
                  data-testid={`fishtype-list-checkbox-${index}`}
                  size="small"
                />
                {fishType.name}
              </StyledTableCell>
              <StyledTableCellAction />
            </TableRow>
          );
        })}
      </>
    );
  };

  const onClick = (checked: boolean) => {
    const allFish = fishTypes?.map((i) => i.id);
    allFish &&
      setConcession({
        ...concession,
        fishTypeIds: checked ? allFish : [],
        assignToAllFishTypes: checked,
      });
  };

  return (
    <>
      <Divider sx={{marginTop: 1}} />
      <ListSubheader disableSticky={true}>{t('fishType')}</ListSubheader>
      <Stack mx={2} direction="row" spacing={2}>
        {/* Assign To All Cages */}
        <FormControlLabel
          control={
            <CircularCheckBox
              checked={concession.assignToAllFishTypes ?? false}
              onClick={(checked) => {
                onClick(checked);
              }}
            />
          }
          label={t('all')}
          data-testid={`assign-to-all-cages`}
        />

        {/* Assign Custom */}
        <FormControlLabel
          control={
            <CircularCheckBox
              checked={!concession.assignToAllFishTypes || false}
              onClick={(checked) => {
                onClick(!checked);
              }}
            />
          }
          label={t('custom')}
          data-testid={'fishtype-custom'}
        />
      </Stack>
      {!concession.assignToAllFishTypes && (
        <StyledContainer>
          <Table sx={{tableLayout: 'auto'}}>
            <TableBody>{fishTypes && <TableLists fishTypes={fishTypes} />}</TableBody>
          </Table>
        </StyledContainer>
      )}
    </>
  );
}

const StyledContainer = styled('div')(({theme}) => ({
  marginTop: theme.spacing(0.35),
  marginBottom: theme.spacing(0.35),
  width: '100%',
  border: '1px solid',
  borderRadius: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  backgroundColor: alpha(theme.palette.background.default, 0.5),
  borderColor: alpha(theme.palette.text.secondary, 0.1),
  '&:hover': {
    backgroundColor: theme.palette.background.default,
    borderColor: theme.palette.text.secondary,
  },
}));

const StyledTableCell = styled(TableCell)(({theme}) => ({
  color: alpha(theme.palette.text.secondary, 1),
  marginTop: theme.spacing(0.35),
  marginBottom: theme.spacing(0.35),
  padding: 0,
  paddingLeft: theme.spacing(2),
  borderColor: alpha(theme.palette.text.secondary, 0.2),
}));
const StyledTableCellAction = styled(TableCell)(({theme}) => ({
  color: alpha(theme.palette.text.secondary, 1),
  marginTop: theme.spacing(0.35),
  marginBottom: theme.spacing(0.35),
  padding: 0,
  paddingRight: 5,
  borderColor: alpha(theme.palette.text.secondary, 0.2),
  maxWidth: '40px',
  textAlign: 'right',
}));

const StyledCheckBox = styled(Checkbox)(({theme}) => ({
  '&.root': {
    marginLeft: theme.spacing(-1),
  },
}));
